/**
 * iter175 — Admin Signup Abuse dashboard.
 *
 * Shows: totals + block rate, breakdown of rejection reasons, top offending
 * IPs, timeseries of accepted vs. rejected, and a live tail of the last 20
 * rejections. Also exposes the "Invite-only mode" toggle for emergencies.
 */
import React, { useEffect, useState } from 'react';
import {
  Shield, Ban, TrendingUp, Loader2, Lock, Unlock, RefreshCcw,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import api from '@/lib/api';

const RANGES = [
  { id: '24h', label: '24h' },
  { id: '7d',  label: '7d'  },
  { id: '30d', label: '30d' },
];

const REASON_LABEL = {
  turnstile_verification_failed: 'Turnstile failed',
  turnstile_missing_token: 'No Turnstile token',
  turnstile_provider_unreachable: 'Turnstile unreachable',
  disposable_email: 'Disposable email',
  weak_password: 'Weak password',
  fake_name: 'Fake name',
  missing_name: 'Missing name',
  min_fill_time: 'Too-fast form fill',
  honeypot_tripped: 'Honeypot tripped',
  velocity_cap: 'Velocity cap hit',
  no_mx_record: 'No MX record',
  email_taken: 'Email already registered',
  invite_only_mode: 'Invite-only mode block',
  stale_form: 'Stale form submission',
};

const SignupAbusePage = () => {
  const [range, setRange] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get(`/admin/signup-abuse/overview?range=${range}`);
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load signup abuse overview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const handleToggleInviteOnly = async (checked) => {
    try {
      setToggling(true);
      const target = checked ? 'invite_only' : 'open';
      await api.post('/admin/signup-abuse/mode', { mode: target });
      toast.success(
        target === 'invite_only'
          ? 'Sign-ups locked to invite-only.'
          : 'Sign-ups open to the public.',
      );
      await load();
    } catch (e) {
      toast.error('Could not update signup mode');
    } finally {
      setToggling(false);
    }
  };

  const totals   = data?.totals   || {};
  const reasons  = data?.reasons  || [];
  const topIps   = data?.top_offending_ips || [];
  const series   = data?.timeseries || [];
  const recent   = data?.recent_rejections || [];
  const isLocked = data?.signup_mode === 'invite_only';

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-signup-abuse-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="h-5 w-5 text-red-400" />
            Signup Abuse
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">
            Real-time visibility into blocked bots + fake sign-ups. Flip invite-only mode when
            you see a wave.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRange(r.id)}
              data-testid={`abuse-range-${r.id}`}
              className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                range === r.id
                  ? 'border-red-500/60 bg-red-950/30 text-red-200'
                  : 'border-slate-700 text-slate-400 hover:text-slate-200'
              }`}
            >
              {r.label}
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            className="ml-2 p-1.5 rounded border border-slate-700 text-slate-400 hover:text-slate-200"
            data-testid="abuse-refresh-btn"
            title="Refresh"
          >
            <RefreshCcw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading…
        </div>
      )}

      {!loading && data && (
        <>
          {/* Mode toggle */}
          <Card
            className={`mb-6 border ${isLocked ? 'bg-rose-950/20 border-rose-800/60' : 'bg-gray-800 border-gray-700'}`}
            data-testid="abuse-mode-card"
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isLocked ? (
                  <Lock className="h-5 w-5 text-rose-400" />
                ) : (
                  <Unlock className="h-5 w-5 text-emerald-400" />
                )}
                <div>
                  <div className="font-semibold text-white">
                    Signup mode: {isLocked ? 'Invite-only' : 'Open'}
                  </div>
                  <div className="text-xs text-slate-400">
                    {isLocked
                      ? 'Only emails with a pending team invite can register.'
                      : 'Anyone can register (subject to Turnstile + all checks).'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">Lock sign-ups</span>
                <Switch
                  checked={isLocked}
                  disabled={toggling}
                  onCheckedChange={handleToggleInviteOnly}
                  data-testid="abuse-mode-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Accepted signups', value: totals.accepted ?? 0 },
              { label: 'Rejected signups', value: totals.rejected ?? 0 },
              { label: 'Block rate',       value: `${totals.block_rate_pct ?? 0}%` },
              { label: 'Reasons tracked',  value: reasons.length },
            ].map((k) => (
              <Card key={k.label} className="bg-gray-800 border-gray-700">
                <CardContent className="p-4">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">{k.label}</div>
                  <div className="text-xl font-semibold text-slate-100 mt-1">{k.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Timeseries */}
          <Card className="mb-6 bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-100 text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-400" />
                Accepted vs. Rejected
              </CardTitle>
            </CardHeader>
            <CardContent>
              {series.length === 0 ? (
                <div className="text-slate-500 text-sm py-6 text-center">
                  No signup traffic yet in this window.
                </div>
              ) : (
                <div style={{ width: '100%', height: 240, minHeight: 220 }}>
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={220}>
                    <LineChart data={series} margin={{ top: 6, right: 16, left: 0, bottom: 6 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1F2A4D" />
                      <XAxis dataKey="day" stroke="#94a3b8" fontSize={11} />
                      <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: '#0F1730', border: '1px solid #334155' }}
                      />
                      <Legend wrapperStyle={{ color: '#94a3b8', fontSize: 12 }} />
                      <Line type="monotone" dataKey="accepted" stroke="#10b981" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="rejected" stroke="#f43f5e" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Reasons breakdown */}
            <Card className="bg-gray-800 border-gray-700" data-testid="abuse-reasons-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-100 text-base flex items-center gap-2">
                  <Ban className="h-4 w-4 text-rose-400" />
                  Rejection reasons
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reasons.length === 0 ? (
                  <div className="text-slate-500 text-sm py-4 text-center">No rejections yet.</div>
                ) : (
                  <div className="space-y-2">
                    {reasons.map((row) => (
                      <div
                        key={row.reason}
                        className="flex items-center justify-between text-sm"
                        data-testid={`abuse-reason-${row.reason}`}
                      >
                        <span className="text-slate-200">
                          {REASON_LABEL[row.reason] || row.reason}
                        </span>
                        <Badge className="bg-slate-700 text-slate-100 text-[10px]">
                          {row.count}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top offending IPs */}
            <Card className="bg-gray-800 border-gray-700" data-testid="abuse-ips-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-100 text-base">Top offending IPs</CardTitle>
              </CardHeader>
              <CardContent>
                {topIps.length === 0 ? (
                  <div className="text-slate-500 text-sm py-4 text-center">Nothing yet.</div>
                ) : (
                  <div className="space-y-2">
                    {topIps.map((row) => (
                      <div
                        key={row.ip}
                        className="flex items-center justify-between text-sm"
                        data-testid={`abuse-ip-${row.ip}`}
                      >
                        <div>
                          <div className="text-slate-200 font-mono">{row.ip}</div>
                          <div className="text-[10px] text-slate-500">
                            Last: {row.last_seen ? new Date(row.last_seen).toLocaleString() : '—'}
                          </div>
                        </div>
                        <Badge className="bg-rose-900/60 text-rose-200 text-[10px]">
                          {row.count}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent rejections log */}
          <Card className="bg-gray-800 border-gray-700" data-testid="abuse-recent-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-100 text-base">Recent rejections (last 20)</CardTitle>
            </CardHeader>
            <CardContent>
              {recent.length === 0 ? (
                <div className="text-slate-500 text-sm py-4 text-center">Quiet — no rejections yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-700">
                      <tr>
                        <th className="py-2 text-left">At</th>
                        <th className="py-2 text-left">Email</th>
                        <th className="py-2 text-left">IP</th>
                        <th className="py-2 text-left">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {recent.map((row, i) => (
                        <tr key={`${row.at}-${i}`}>
                          <td className="py-1.5 text-slate-400">
                            {row.at ? new Date(row.at).toLocaleString() : '—'}
                          </td>
                          <td className="py-1.5 text-slate-200">{row.email || '—'}</td>
                          <td className="py-1.5 text-slate-300 font-mono">{row.ip || '—'}</td>
                          <td className="py-1.5">
                            <span className="text-rose-300">
                              {REASON_LABEL[row.reason] || row.reason || 'unknown'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default SignupAbusePage;
